/*
 * Buying a plan as a gift: what goes on the order, what the browser keeps
 * between the plan page, checkout and the success screen, and the ways a
 * gift link leaves the page.
 *
 * Mirrors the Android app (fleek_kotlin feature/gift): the same limits and
 * the same request shape.
 */

/**
 * Every new web gift is "plain": one gift look and wording in every store,
 * India included, with no festival on it. The backend's other theme,
 * "diwali", is still rendered for gifts already created with it (old links
 * keep working), but the web never creates one.
 */
export const GIFT_THEME = "plain";

/** Whether a gift (or its theme string) is one of the older Diwali gifts. */
export const isDiwaliGift = (theme) => String(theme || "").toLowerCase() === "diwali";
/** How long a name may be. The server clips to the same number. */
export const GIFT_NAME_MAX = 40;
/** The message box's cap. The server enforces the same number. */
export const GIFT_MESSAGE_MAX = 240;

const DRAFT_KEY = "fleek_gift_draft";
const ORDERS_KEY = "fleek_gift_orders";
const CREATED_KEY = "fleek_gift_created";
const MAX_REMEMBERED = 20;

const clip = (value, max) => String(value || "").trim().slice(0, max);

export const firstName = (name) => String(name || "").trim().split(/\s+/)[0] || "";

const read = (store, key) => {
  try {
    return JSON.parse(store.getItem(key) || "null");
  } catch {
    return null;
  }
};
const write = (store, key, value) => {
  try {
    if (value == null) store.removeItem(key);
    else store.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked: the gift still works in this page, it just won't survive a reload */
  }
};

/** The `gift` block of a create-order request, from what the sheet collected. */
export const giftRequest = ({ sender, recipient, message } = {}) => ({
  sender_name: clip(sender, GIFT_NAME_MAX),
  recipient_name: clip(recipient, GIFT_NAME_MAX),
  message: clip(message, GIFT_MESSAGE_MAX),
  theme: GIFT_THEME,
});

/**
 * The create-order body for a gift, from checkout's ordinary one.
 *
 * The server takes a gift only on a buy_once order of one plan with no
 * phone_number: a number would activate the plan on it at payment and skip
 * the link. So the number, a bundle, and the USD phone details all go.
 */
export const giftOrderBody = (base, gift) => {
  const body = { ...base, screen_type: "buy_once", gift };
  delete body.phone_number;
  delete body.details;
  delete body.bundle;
  delete body.buy_now;
  return body;
};

/* ---- Draft: written on the plan page, read at checkout ---- */

/** What the buyer wrote, held for the checkout of this one plan. */
export const saveGiftDraft = (planId, gift) =>
  write(sessionStorage, DRAFT_KEY, { plan_id: String(planId), gift });

/**
 * The gift for the plan in the cart, or null. A draft for a different plan is
 * stale (the cart changed since) and is dropped rather than applied.
 */
export const giftDraftFor = (planId) => {
  const draft = read(sessionStorage, DRAFT_KEY);
  if (!draft?.gift) return null;
  if (planId == null || String(planId) !== draft.plan_id) {
    write(sessionStorage, DRAFT_KEY, null);
    return null;
  }
  return draft.gift;
};

export const clearGiftDraft = () => write(sessionStorage, DRAFT_KEY, null);

/* ---- Orders placed as gifts, so the success screen knows ---- */

export const rememberGiftOrder = (orderId) => {
  if (!orderId) return;
  const ids = (read(localStorage, ORDERS_KEY) || []).filter((id) => id !== String(orderId));
  write(localStorage, ORDERS_KEY, [String(orderId), ...ids].slice(0, MAX_REMEMBERED));
};

export const isRememberedGiftOrder = (orderId) =>
  !!orderId && (read(localStorage, ORDERS_KEY) || []).includes(String(orderId));

/** An order row (get_orders_from_payment_id or my_orders) that carries a gift. */
export const isGiftOrderRow = (row) =>
  !!(row?.gift || row?.details?.gift || (row?.new_ordered_items || []).some((i) => i?.gift));

/** gift_created counts once per order, whatever reloads the screen. */
export const markGiftCreated = (orderId) => {
  const ids = read(localStorage, CREATED_KEY) || [];
  if (ids.includes(String(orderId))) return false;
  write(localStorage, CREATED_KEY, [String(orderId), ...ids].slice(0, MAX_REMEMBERED));
  return true;
};

/* ---- Sending ---- */

export const whatsAppUrl = (text) => `https://wa.me/?text=${encodeURIComponent(text || "")}`;

/** The phone's own share sheet, where there is one. Desktop browsers that have
    navigator.share offer a poorer sheet than WhatsApp Web, so phones only. */
export const canNativeShare = () =>
  typeof navigator !== "undefined" &&
  typeof navigator.share === "function" &&
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");

/** Copies text, with the old textarea route for browsers without the async clipboard. */
export const copyText = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    el.remove();
    return ok;
  } catch {
    return false;
  }
};

/** Plan length for the card: "12 months", "1 month". */
export const durationText = (plan = {}) => {
  const n = Number(plan.duration);
  const unit = String(plan.duration_type || "").trim().toLowerCase();
  if (!n || !unit) return "";
  const singular = unit.replace(/s$/, "");
  return `${n} ${n === 1 ? singular : `${singular}s`}`;
};
